import {ARCHIVE_RECORD, ARCHIVE_RECORD_GQL} from 'archive/__tests__/ArchiveRecord-fixtures';
import {Permissions} from 'auth/PermissionConstants';
import SampleType from 'constants/SampleType';
import {AutomationInputFileConfig} from 'lab-automation/automation-files/LabAutomationConstants';
import {
  ASSAY_TYPES,
  AssayRunSchema,
  AssaySchema,
  ResultSchema,
} from 'results/assay-schemas/AssaySchemaConstants';
import {
  BatchSampleLinkSchemaField,
  BioentitySampleLinkSchemaField,
  FieldType,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {ORGANIZATION} from 'test/fixtures/parties/OrganizationFixtures';

import {ResultSchemaGraphQL} from '../../../graphql-fragments/results/schemas/__generated/ResultSchemaGraphQL';
import {RunSchemaGraphQL} from '../../../graphql-fragments/runs/schemas/__generated/RunSchemaGraphQL';
import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';

export const ASSAY_SCHEMA_PERMISSIONS: Permissions = {
  admin: false,
  appendable: true,
  archivable: true,
  definitionDeletable: true,
  definitionEditable: true,
  definitionListable: true,
  objectsCreatable: true,
  objectsRegisterable: true,
  readable: true,
  unarchivable: false,
  writable: true,
};

export const ASSAY_SCHEMA_PERMISSIONS_GQL: SchemaPermissionsGraphQL = {
  __typename: 'PermissionsNode',
  admin: false,
  appendable: true,
  archivable: true,
  definitionDeletable: true,
  definitionEditable: true,
  definitionListable: true,
  objectsCreatable: true,
  objectsRegisterable: true,
  readable: true,
  unarchivable: false,
  writable: true,
};

export const ASSAY_SCHEMA_FIELD: SchemaField = {
  archiveRecord: null,
  id: 1,
  dbId: 1,
  apiId: 'tsf_111',
  name: 'my_count',
  systemName: 'my_count',
  displayName: 'My Count',
  fieldType: FieldType.INTEGER,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
  description: null,
  tooltipText: 'This is a tooltip',
  unitApiIdentifier: null,
};

export const ARCHIVED_FIELD: SchemaField = {
  id: 2,
  dbId: 2,
  apiId: 'tsf_112',
  name: 'my_number',
  systemName: 'my_number',
  displayName: 'My Number',
  fieldType: FieldType.INTEGER,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
  archiveRecord: {
    purpose: 'Archived',
  },
};

export const BOOLEAN_FIELD: SchemaField = {
  archiveRecord: null,
  id: 3,
  dbId: 3,
  apiId: 'tsf_113',
  name: 'my_bool',
  systemName: 'my_bool',
  displayName: 'My Boolean',
  fieldType: FieldType.BOOLEAN,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const DATETIME_FIELD: SchemaField = {
  archiveRecord: null,
  id: 4,
  dbId: 4,
  apiId: 'tsf_114',
  name: 'my_datetime',
  systemName: 'my_datetime',
  displayName: 'My Datetime',
  fieldType: FieldType.DATETIME,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

export const JSON_FIELD: SchemaField = {
  archiveRecord: null,
  id: 5,
  dbId: 5,
  apiId: 'tsf_115',
  name: 'my_json',
  systemName: 'my_json',
  displayName: 'My Json',
  fieldType: FieldType.JSON,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: null,
};

// Batches are deprecated: we are preventing new batch link field creation and will soon make all batch link
// fields inaccessible in the UI (go/batch-deprecation-2025). Unless necessary to test deprecation, avoid
// adding tests that support batch link fields.
export const BATCH_LINK_FIELD: SchemaField = {
  archiveRecord: null,
  id: 8471,
  dbId: 8471,
  apiId: 'tsf_001_batch_link',
  name: 'ft_batch_link',
  systemName: 'ft_batch_link',
  displayName: 'ft_batch_link',
  fieldType: FieldType.BATCH_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isIntegration: false,
  isMulti: true,
  isParentLink: false,
  isSnapshot: false,
  schemaFieldSelectorId: null,
  requiredLink: {
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
    tagSchema: null,
  },
};

export const ENTRY_LINK_FIELD: SchemaField = {
  archiveRecord: null,
  id: 54,
  dbId: 54,
  apiId: 'tsf_001_entry_link',
  name: 'ft_entry_link',
  systemName: 'ft_entry_link',
  displayName: 'ft_entry_link',
  fieldType: FieldType.ENTRY_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isIntegration: false,
  isMulti: true,
  isParentLink: false,
  isSnapshot: false,
  schemaFieldSelectorId: null,
  requiredLink: {
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
    tagSchema: null,
  },
};

export const RUN_LINK_FIELD: SchemaField = {
  archiveRecord: null,
  id: 3,
  dbId: 3,
  apiId: 'tsf_113_run_link',
  name: 'run_link_field',
  systemName: 'run_link_field',
  displayName: 'Run Link Field',
  fieldType: FieldType.ASSAY_RUN_LINK,
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  requiredLink: {
    assaySchema: null,
  },
};

export const SAMPLES_SCHEMA_FIELD: BioentitySampleLinkSchemaField = {
  fieldType: FieldType.SAMPLE_LINK,
  requiredLink: {
    sampleType: SampleType.BIOENTITY,
    tagSchema: null,
  },
  archiveRecord: null,
  id: 7,
  dbId: 7,
  apiId: 'tsf_117',
  name: 'sample_entity',
  systemName: 'sample_entity',
  displayName: 'Sample Entity',
  isRequired: false,
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
  isMulti: false,
  schemaFieldSelectorId: null,
  description: null,
  tooltipText: null,
  unitApiIdentifier: null,
};

// Batches are deprecated. Only needed for tests to ensure that users cannot input a new batch link field.
// Unless necessary for go/batch-deprecation-2025, avoid adding tests that support batch link fields.
export const BATCH_SAMPLES_SCHEMA_FIELD: BatchSampleLinkSchemaField = {
  ...BATCH_LINK_FIELD,
  // Overwrite some properties to match the BatchSampleLinkSchemaField type
  fieldType: FieldType.SAMPLE_LINK,
  requiredLink: {
    sampleType: SampleType.BATCH,
    batchSchema: null,
  },
};

const DEFAULT_RUN_SCHEMA_PROPS = {
  automationOutputFileConfigs: [],
  automationInputFileConfigs: [],
  robotInstructionsConfig: null,
  allowCreateRunFromNlad: true,
  allowInsertRunFromInbox: true,
  permissions: ASSAY_SCHEMA_PERMISSIONS,
  connectionSchema: null,
  shouldRecordEquipment: false,
};

export const FLOW_RUN_SCHEMA: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_1',
  name: 'Flow Cytometry Run',
  requestTaskSchemaIds: [],
  derivedParent: null,
  sampleGroupSchema: null,
  sqlIdentifier: 'flow_cytometry_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [ASSAY_SCHEMA_FIELD],
  fields: [ASSAY_SCHEMA_FIELD],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
  automationOutputFileConfigs: [],
  automationInputFileConfigs: [],
  robotInstructionsConfig: null,
  allowInsertRunFromInbox: true,
  allowCreateRunFromNlad: true,
  shouldRecordEquipment: false,
};

const DEFAULT_RUN_SCHEMA_GQL_PROPS = {
  automationInputFileConfigsJson: [],
  automationOutputFileConfigsJson: [],
  robotInstructionsConfigJson: null,
  allowCreateRunFromNlad: true,
  allowInsertRunFromInbox: true,
  permissions: ASSAY_SCHEMA_PERMISSIONS_GQL,
  connectionSchema: null,
  shouldRecordEquipment: false,
};

export const FLOW_RUN_SCHEMA_GQL: RunSchemaGraphQL = {
  ...DEFAULT_RUN_SCHEMA_GQL_PROPS,
  __typename: 'RunSchemaNode',
  id: 'assaysch_1',
  name: 'Flow Cytometry Run',
  derivedParent: null,
  sqlIdentifier: 'flow_cytometry_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organizationJson: ORGANIZATION,
  allFieldsJson: [ASSAY_SCHEMA_FIELD],
  fieldsJson: [ASSAY_SCHEMA_FIELD],
  archiveRecord: null,
  automationInputFileConfigsJson: [],
  automationOutputFileConfigsJson: [],
  robotInstructionsConfigJson: null,
  allowInsertRunFromInbox: true,
  allowCreateRunFromNlad: true,
  shouldRecordEquipment: false,
};

export const RUN_SCHEMA_WITH_DERIVED_PARENT: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_8',
  name: 'Run with derived parent',
  derivedParent: {
    id: 'assaysch_1',
    name: 'Flow Cytometry Run',
    sqlIdentifier: 'flow_cytometry_run',
  },
  sqlIdentifier: 'flow_cytometry_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [ASSAY_SCHEMA_FIELD],
  fields: [ASSAY_SCHEMA_FIELD],
  archiveRecord: null,
  prefix: '',
  publishedDataTableColumns: null,
  requestTaskSchemaIds: [],
  requestTemplateIds: [],
  sampleGroupSchema: null,
  allowInsertRunFromInbox: true,
  allowCreateRunFromNlad: true,
  shouldRecordEquipment: false,
};

export const RUN_SCHEMA_WITH_DERIVED_PARENT_GQL: RunSchemaGraphQL = {
  ...DEFAULT_RUN_SCHEMA_GQL_PROPS,
  __typename: 'RunSchemaNode',
  id: 'assaysch_8',
  name: 'Run with derived parent',
  derivedParent: {
    __typename: 'RunSchemaNode',
    id: 'assaysch_1',
    name: 'Flow Cytometry Run',
    sqlIdentifier: 'flow_cytometry_run',
  },
  sqlIdentifier: 'flow_cytometry_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organizationJson: ORGANIZATION,
  allFieldsJson: [ASSAY_SCHEMA_FIELD],
  fieldsJson: [ASSAY_SCHEMA_FIELD],
  archiveRecord: null,
};

export const CELL_SAMPLE_PREP_RUN_SCHEMA: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_2',
  name: 'Cell Sample Prep Run',
  requestTaskSchemaIds: [],
  derivedParent: null,
  sampleGroupSchema: null,
  sqlIdentifier: 'cell_sample_prep_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
};

export const NGS_RUN_SCHEMA: AssayRunSchema & {robotInstructionsConfig: AutomationInputFileConfig | null} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_3',
  name: 'NGS Run',
  requestTaskSchemaIds: [],
  derivedParent: null,
  sampleGroupSchema: null,
  sqlIdentifier: 'ngs_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
};

export const FLOW_CYTOMETRY_RESULT: ResultSchema = {
  id: 'assaysch_4',
  name: 'Flow Cytometry Result',
  requestTaskSchemaIds: [],
  derivedParent: null,
  sampleGroupSchema: null,
  sqlIdentifier: 'flow_cytometry_result',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

// Keep in sync with assayRecords.ts
export const ASSAY_SCHEMAS_BY_ID: {[id: string]: AssaySchema} = {
  assaysch_1: FLOW_RUN_SCHEMA,
  assaysch_2: CELL_SAMPLE_PREP_RUN_SCHEMA,
  assaysch_3: NGS_RUN_SCHEMA,
  assaysch_4: FLOW_CYTOMETRY_RESULT,
};

export const RESULT_SCHEMA: ResultSchema = {
  id: 'assaysch_result_1',
  name: 'Result Schema 1',
  requestTaskSchemaIds: [],
  derivedParent: null,
  sampleGroupSchema: null,
  sqlIdentifier: 'an_ending',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  prefix: '',
  requestTemplateIds: [],
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RESULT_SCHEMA_2: ResultSchema = {
  allFields: [ENTRY_LINK_FIELD, BATCH_LINK_FIELD],
  fields: [ENTRY_LINK_FIELD, BATCH_LINK_FIELD],
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  derivedParent: null,
  organization: ORGANIZATION,
  prefix: '',
  publishedDataTableColumns: undefined,
  requestTaskSchemaIds: [],
  requestTemplateIds: [],
  id: 'assay_result_schema_2',
  name: 'Result Schema 2',
  sqlIdentifier: 'result_schema_2',
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RESULT_SCHEMA_GQL: ResultSchemaGraphQL = {
  __typename: 'ResultSchemaNode',
  id: 'assaysch_result_1',
  name: 'Result Schema 1',
  derivedParent: null,
  sqlIdentifier: 'an_ending',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organizationJson: ORGANIZATION,
  allFieldsJson: [],
  fieldsJson: [],
  publishedDataTableColumnsJson: null,
  archiveRecord: null,
  permissions: ASSAY_SCHEMA_PERMISSIONS_GQL,
};

export const RESULT_SCHEMA_WITH_FIELD_GQL: ResultSchemaGraphQL = {
  ...RESULT_SCHEMA_GQL,
  allFieldsJson: [ASSAY_SCHEMA_FIELD],
};

export const ARCHIVED_RESULT_SCHEMA_GQL: ResultSchemaGraphQL = {
  ...RESULT_SCHEMA_GQL,
  id: 'assaysch_archived',
  name: 'Archived Result Schema',
  archiveRecord: ARCHIVE_RECORD_GQL,
};

export const RUN_SCHEMA: AssayRunSchema & {robotInstructionsConfig: AutomationInputFileConfig | null} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_run_1',
  name: 'Run Schema 1',
  requestTaskSchemaIds: [],
  derivedParent: null,
  sampleGroupSchema: null,
  sqlIdentifier: 'overdue',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  requestTemplateIds: [],
  prefix: '',
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RUN_SCHEMA_GQL: RunSchemaGraphQL = {
  __typename: 'RunSchemaNode',
  id: 'assaysch_run_1',
  name: 'Run Schema 1',
  derivedParent: null,
  sqlIdentifier: 'overdue',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organizationJson: ORGANIZATION,
  allFieldsJson: [],
  fieldsJson: [],
  archiveRecord: null,
  permissions: ASSAY_SCHEMA_PERMISSIONS_GQL,
  automationInputFileConfigsJson: [],
  automationOutputFileConfigsJson: [],
  robotInstructionsConfigJson: null,
  allowCreateRunFromNlad: true,
  allowInsertRunFromInbox: true,
  connectionSchema: null,
  shouldRecordEquipment: false,
};

export const ARCHIVED_RESULT_SCHEMA: ResultSchema = {
  ...RESULT_SCHEMA,
  id: 'assaysch_archived_result_1',
  name: 'Archived Result Schema 1',
  archiveRecord: ARCHIVE_RECORD,
};

export const ARCHIVED_RUN_SCHEMA: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...RUN_SCHEMA,
  id: 'assaysch_archived_run_1',
  name: 'Archived Run Schema 1',
  archiveRecord: ARCHIVE_RECORD,
};

export const RESULT_SCHEMA_WITH_ARCHIVED_FIELD: ResultSchema = {
  id: 'assaysch_5',
  name: 'Flow Cytometry Result',
  requestTaskSchemaIds: [],
  derivedParent: null,
  sampleGroupSchema: null,
  sqlIdentifier: 'flow_cytometry_result',
  schemaType: ASSAY_TYPES.ASSAY_RESULT,
  organization: ORGANIZATION,
  allFields: [ARCHIVED_FIELD],
  fields: [],
  publishedDataTableColumns: null,
  archiveRecord: null,
  requestTemplateIds: [],
  prefix: null,
  permissions: ASSAY_SCHEMA_PERMISSIONS,
};

export const RUN_SCHEMA_WITH_ARCHIVED_FIELD: AssayRunSchema & {
  robotInstructionsConfig: AutomationInputFileConfig | null;
} = {
  ...DEFAULT_RUN_SCHEMA_PROPS,
  id: 'assaysch_6',
  name: 'Flow Cytometry Run',
  requestTaskSchemaIds: [],
  derivedParent: null,
  sampleGroupSchema: null,
  sqlIdentifier: 'flow_cytometry_run',
  schemaType: ASSAY_TYPES.ASSAY_RUN,
  organization: ORGANIZATION,
  allFields: [ASSAY_SCHEMA_FIELD, ARCHIVED_FIELD],
  fields: [ASSAY_SCHEMA_FIELD],
  publishedDataTableColumns: null,
  archiveRecord: null,
  requestTemplateIds: [],
  prefix: null,
};

export const UNCREATEABLE_RUN_SCHEMA = {
  ...RUN_SCHEMA,
  id: 'assaysch_run_29123993',
  name: 'Uncreateable Run Schema',
  permissions: {
    ...ASSAY_SCHEMA_PERMISSIONS_GQL,
    objectsCreatable: false,
  },
};

export const UNCREATEABLE_RESULT_SCHEMA: ResultSchema = {
  ...RESULT_SCHEMA,
  id: 'assaysch_result_29123993',
  name: 'Uncreateable Result Schema',
  permissions: {
    ...ASSAY_SCHEMA_PERMISSIONS,
    objectsCreatable: false,
  },
};
